import { MockedAuthManager } from "../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing";
import React, { useMemo } from "react";
import { createStackNavigator } from "@react-navigation/stack";

import {
    buildClient,
    buildClientProfile,
    buildClientUser,
    buildIjmGroup,
    buildMission,
    buildUser,
    getAvatarUrl,
} from "../factories";
import type { Meta, StoryObj } from "@storybook/react";
import { Button, View } from "react-native";
import Callout from "../../ui_atoms/Callout";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import { StackScreenProps } from "@react-navigation/stack";
import MissionSelectSignerScreen from "../../screens/user/MissionSelectSignerScreen";
import { GET_CLIENT_USERS } from "../../graphql/client";
import { ProfileType } from "../../__generated__/graphql";
import { CHANGE_MISSION_SIGNER } from "../../graphql/core";

type StoryStackParams = {
    Home: undefined;
    MissionSelectSignerScreen: { missionId: string };
};

const HomeFakeScreen: React.FC<StackScreenProps<StoryStackParams, "Home">> = ({ navigation }) => {
    return (
        <View>
            <Callout markdown="Home screen" />
            <Button
                title="Back to preview"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [
                            {
                                name: "MissionSelectSignerScreen",
                                params: { missionId: "test-mission" },
                            },
                        ],
                    });
                }}
            />
        </View>
    );
};

const Stack = createStackNavigator<StoryStackParams>();

const StoryComponent = () => {
    const user = useMemo(
        () =>
            buildUser({
                clientProfiles: [
                    buildClientProfile({
                        id: "client",
                        client: buildClient({
                            canChangeIjmGroup: true,
                            ijmGroup: buildIjmGroup({
                                id: "GM-G1-730-2",
                                name: "2 jours de délai d'attente",
                            }),
                            ijmPayFirstDays: true,
                            ijmPayRemainingPercents: 20,
                        }),
                    }),
                ],
            }),
        [],
    );
    const mock = useMemo(
        () => [
            {
                variableMatcher: () => true,
                request: {
                    query: GET_CLIENT_USERS,
                },
                newData: () => ({
                    data: {
                        getClientUsers: [
                            buildClientUser({ id: "the-client-profile-owner" }),
                            buildClientUser({
                                avatarUrl: getAvatarUrl(),
                                firstName: "Mokoto",
                                lastName: "Kusanagi",
                            }),
                            buildClientUser({
                                id: "client",
                                avatarUrl: getAvatarUrl(),
                                firstName: "Self",
                                lastName: "Should not display",
                            }),
                        ],
                    },
                }),
            },
            {
                variableMatcher: () => true,
                request: {
                    query: CHANGE_MISSION_SIGNER,
                },
                newData: () => ({
                    data: {
                        changeMissionSigner: buildMission(),
                    },
                }),
            },
        ],
        [],
    );

    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={ProfileType.Client} profileId={"client"}>
                <MockedProvider mocks={mock}>
                    <Stack.Navigator initialRouteName="MissionSelectSignerScreen">
                        <Stack.Screen
                            name="MissionSelectSignerScreen"
                            component={MissionSelectSignerScreen}
                            initialParams={{ missionId: `test-mission` }}
                            options={{
                                gestureEnabled: false,
                                headerShown: true,
                                title: "",
                                headerBackTitle: "Back",
                                headerBackButtonDisplayMode: "default",
                            }}
                        />
                        <Stack.Screen name="Home" component={HomeFakeScreen} />
                    </Stack.Navigator>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/Change Signer",
    component: StoryComponent,
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Default: Story = {};
